import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useHttp } from 'hooks/http.hook';
import { USEFUL } from 'endpointsConst';
import PageContainer from 'components/common/PageContainer';
import PageTitle from 'components/common/Layout/PageTitle';
import SEO from 'components/common/SEO';
import Loading from '../../Loading';
import UsefulCard from './UsefulCard';
import { UsefulMaterialListItem } from './types';

import styles from './styles.module.scss';

// On a narrow screen the design shows four cards and a button that opens the rest
const MOBILE_VISIBLE_COUNT = 4;

const UsefulList: React.FC = () => {
  const { t } = useTranslation();
  const { request, loading } = useHttp();
  const [materials, setMaterials] = useState<UsefulMaterialListItem[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    request(USEFUL)
      .then((res: any) => {
        setMaterials(Array.isArray(res?.data?.items) ? res.data.items : []);
      })
      .catch(() => {
        setMaterials([]);
      });
  }, [request]);

  // The whole list is always rendered, the extra cards are only hidden by CSS on mobile —
  // that keeps every link in the markup for search engines
  const hasCollapsedCards = !expanded && (materials?.length ?? 0) > MOBILE_VISIBLE_COUNT;

  return (
    <PageContainer>
      {/* Title and description come from the /useful entry in seoRoutes.json */}
      <SEO canonicalUrl={`${window.location.origin}/useful`} />
      <div className={styles.content}>
        <PageTitle>{t('useful.heading')}</PageTitle>

        {loading && <Loading />}

        {materials && materials.length > 0 && (
          <>
            <div className={styles.grid}>
              {materials.map((material, index) => (
                <div
                  key={material.id}
                  className={cn(styles.gridItem, {
                    [styles.gridItemCollapsed]: hasCollapsedCards && index >= MOBILE_VISIBLE_COUNT,
                  })}
                >
                  <UsefulCard material={material} />
                </div>
              ))}
            </div>

            {hasCollapsedCards && (
              <button className={styles.expandButton} onClick={() => setExpanded(true)}>
                {t('useful.allArticles')}
              </button>
            )}
          </>
        )}

        {materials && materials.length === 0 && !loading && <p className={styles.empty}>{t('useful.empty')}</p>}
      </div>
    </PageContainer>
  );
};

export default UsefulList;
